/**
 * Central HTTP-client för Prime RMS backend-anrop (INFRA-112-grund).
 *
 * Konventioner:
 *   - Alla requests går till /api/* (same-origin via Caddy-proxy)
 *   - credentials: 'include' så HttpOnly session-cookies skickas med
 *   - Läser X-Correlation-Id-header från response för spårbarhet
 *   - Kastar HttpError med status + code + message + correlationId
 *
 * Används inte direkt av services idag — wrappers i src/api/<resurs>.js
 * hanterar rutt-specifika detaljer. När BACKEND_MODE=true kan services
 * importera från src/api/ istället för StorageAdapter.
 *
 * Status: INFRASTRUKTUR. Ingen service använder detta ännu. Aktiveras
 * stegvis per service när produktägaren är redo att migrera.
 */

import { HttpError } from './errors';

// Kan override:as för test — annars relativ path (same-origin via Caddy)
const DEFAULT_BASE = '';

let runtimeBaseUrl = DEFAULT_BASE;
let tenantResolver = null;

export function setBaseUrl(url) {
  runtimeBaseUrl = url || DEFAULT_BASE;
}

export function getBaseUrl() {
  return runtimeBaseUrl;
}

/**
 * Registrera en funktion som returnerar default-tenant-id för automatisk
 * X-Tenant-Id-header. Används tills backend-sessionen har tenant-claim.
 * App.js anropar setDefaultTenantResolver(() => entityService.getActiveEntityId())
 * vid init. Endpoint-anropare kan fortfarande skicka `opts.tenantId` explicit
 * för att overrida.
 */
export function setDefaultTenantResolver(fn) {
  tenantResolver = typeof fn === 'function' ? fn : null;
}

/**
 * Core fetch-wrapper. Interna rutt-wrappers kallar alltid genom denna.
 *
 * @param {string} path - Relativ path från /api, t.ex. "entities" eller "risks/R001"
 * @param {object} opts
 * @param {string} opts.method - HTTP-verb (default GET)
 * @param {object} opts.body - JSON-body, automatiskt JSON.stringify:ad
 * @param {object} opts.query - Query-params { key: value }
 * @param {object} opts.headers - Custom headers
 * @param {string} opts.tenantId - Sätt X-Tenant-Id (dev-läge tills session används)
 * @param {AbortSignal} opts.signal - För timeouts/avbryt
 * @returns {Promise<{data, correlationId, status}>} — data är response-body.data
 */
export async function request(path, opts = {}) {
  const method = (opts.method || 'GET').toUpperCase();
  const headers = new Headers(opts.headers || {});

  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (opts.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  // Tenant-header: explicit opts.tenantId har prioritet, annars resolver (om satt)
  const effectiveTenantId = opts.tenantId !== undefined && opts.tenantId !== null
    ? opts.tenantId
    : (tenantResolver ? tenantResolver() : null);
  if (effectiveTenantId) headers.set('X-Tenant-Id', effectiveTenantId);

  const url = new URL(
    `${runtimeBaseUrl}/api/${path.replace(/^\/+/, '')}`,
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
  );
  if (opts.query) {
    for (const [key, val] of Object.entries(opts.query)) {
      if (val === undefined || val === null || val === '') continue;
      url.searchParams.set(key, String(val));
    }
  }

  let response;
  try {
    response = await fetch(url.toString(), {
      method,
      headers,
      credentials: 'include',
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    // Network error, DNS-fel, CORS
    throw new HttpError(`Network error: ${err.message}`, {
      status: 0,
      code: 'network_error',
      cause: err,
    });
  }

  const correlationId = response.headers.get('x-correlation-id') || null;
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');

  if (response.status === 204) {
    return { data: null, correlationId, status: 204 };
  }

  const payload = isJson ? await response.json().catch(() => null) : await response.text();

  if (!response.ok) {
    const message = (payload && payload.message) || `${method} ${path} failed (${response.status})`;
    throw new HttpError(message, {
      status: response.status,
      code: (payload && payload.error) || 'http_error',
      details: payload && payload.details,
      correlationId,
    });
  }

  // Backend-konvention: { data, ... } eller råvärde. Returnera hela payload så
  // endpoint-wrappers kan välja hur de unpackar.
  return { data: payload && payload.data !== undefined ? payload.data : payload, raw: payload, correlationId, status: response.status };
}

export const http = {
  get: (path, opts = {}) => request(path, { ...opts, method: 'GET' }),
  post: (path, body, opts = {}) => request(path, { ...opts, method: 'POST', body }),
  patch: (path, body, opts = {}) => request(path, { ...opts, method: 'PATCH', body }),
  put: (path, body, opts = {}) => request(path, { ...opts, method: 'PUT', body }),
  delete: (path, opts = {}) => request(path, { ...opts, method: 'DELETE' }),
};
